import { initialState } from '../initialState.js';
import { createSlice } from '@reduxjs/toolkit';
import {
  createDiagnostic,
  createNewCar,
  createVoiceDiagnostic,
  editVINorMileage,
  getAllCars,
  getCarInfoByPlateNumber,
  getDiagnostic,
  getMileageOrVinFromPhoto,
  getNodesAndParts,
  getRepair,
  recognizeLicensePlate,
  updateCar,
  updateRepair,
  uploadCarPhotos,
  uploadMedia,
  updateRepairPartStatus,
  getMainDiagnostic,
  updateRepairServiceStatus,
} from './operations.js';

const handlePending = state => {
  state.isLoading = true;
  state.error = null;
};

const handleRejected = (state, action) => {
  {
    state.isLoading = false;
    state.error = action.payload;
  }
};

const carsSlice = createSlice({
  name: 'cars',
  initialState: initialState.cars,
  reducers: {
    setChosenDate: (state, action) => {
      state.chosenDate = action.payload;
    },
    clearChosenDate: state => {
      state.chosenDate = '';
    },
    deleteCarInfo: state => {
      state.carInfo = {};
      state.isRecognitionLoading = false;
      state.error = null;
    },
    deleteMileageOrVin: state => {
      state.mileageOrVin = {};
      state.isMileageOrVinLoading = false;
      state.error = null;
    },
    deleteNewCar: state => {
      state.isSavingCarLoading = false;
      state.error = null;
      state.newCar = {};
    },
    clearDiag: state => {
      state.diagnostic = {};
    },
    clearRepair: state => {
      state.repairDetails = {};
    },
    clearVoiceDiag: state => {
      state.voiceDiagnostic = {};
    },
    clearCarData: state => {
      state.carInfo = {};
    },
    clearMileageOrVin: state => {
      state.mileageOrVin = {};
    },
  },
  extraReducers: builder =>
    builder
      // Get all cars +
      .addCase(getAllCars.pending, handlePending)
      .addCase(getAllCars.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cars = action.payload.records;
      })
      .addCase(getAllCars.rejected, handleRejected)

      // Recognize license plate +
      .addCase(recognizeLicensePlate.pending, state => {
        state.isRecognitionLoading = true;
        state.error = null;
      })
      .addCase(recognizeLicensePlate.fulfilled, (state, action) => {
        state.isRecognitionLoading = false;
        if (!action.payload.car_info) {
          state.carInfo.plate = action.payload.number_plate;
        } else {
          state.carInfo = action.payload.car_info.car;
        }
      })
      .addCase(recognizeLicensePlate.rejected, (state, action) => {
        state.isRecognitionLoading = false;
        state.error = action.payload;
      })

      // Get car by plate number +
      .addCase(getCarInfoByPlateNumber.pending, state => {
        state.isInfoByPlateLoading = true;
        state.error = null;
      })
      .addCase(getCarInfoByPlateNumber.fulfilled, (state, action) => {
        state.isInfoByPlateLoading = false;
        state.carInfo = action.payload.car;
        state.additionalInfo = {
          in_progress: action.payload.in_progress,
          detail: action.payload.detail,
          record_service: action.payload.record_service,
        };
      })
      .addCase(getCarInfoByPlateNumber.rejected, (state, action) => {
        state.isInfoByPlateLoading = false;
        state.error = action.payload;
      })

      // Edit VIN or mileage +
      .addCase(editVINorMileage.pending, handlePending)
      .addCase(editVINorMileage.fulfilled, (state, action) => {
        state.isLoading = false;
        const { car_id, vin, mileage } = action.meta.arg;

        const carToEdit = state.cars.find(car => car.car_id === car_id);

        if (carToEdit && vin) {
          carToEdit.vin = vin;
        }
        if (carToEdit && mileage) {
          carToEdit.mileage = mileage;
        }
      })

      //! DIAGNOSTICS
      // Get Nodes and Parts list +
      .addCase(getNodesAndParts.pending, handlePending)
      .addCase(getNodesAndParts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.nodesAndPartsForDiagnostics = action.payload.tree.nodes;
      })
      .addCase(getNodesAndParts.rejected, handleRejected)

      // Get particular diagnostic +
      .addCase(getDiagnostic.pending, (state, action) => {
        state.isDiagLoading = true;
        state.error = null;
      })
      .addCase(getDiagnostic.fulfilled, (state, action) => {
        state.isDiagLoading = false;
        state.diagnostic = action.payload;
      })
      .addCase(getDiagnostic.rejected, (state, action) => {
        state.isDiagLoading = false;
        state.error = action.payload;
      })

      .addCase(getMainDiagnostic.pending, (state, action) => {
        state.isDiagLoading = true;
        state.error = null;
      })
      .addCase(getMainDiagnostic.fulfilled, (state, action) => {
        state.isDiagLoading = false;
        state.diagnostic = action.payload;
      })
      .addCase(getMainDiagnostic.rejected, (state, action) => {
        state.isDiagLoading = false;
        state.error = action.payload;
      })

      // Create diagnostic +
      .addCase(createDiagnostic.pending, (state, action) => {
        state.isDiagCreateLoading = true;
        state.error = null;
      })
      .addCase(createDiagnostic.fulfilled, (state, action) => {
        state.isDiagCreateLoading = false;
      })
      .addCase(createDiagnostic.rejected, (state, action) => {
        state.isDiagCreateLoading = false;
        state.error = action.payload;
      })

      // Create Voice Diagnostic +
      .addCase(createVoiceDiagnostic.pending, state => {
        state.isVoiceDiagLoading = true;
        state.error = null;
      })
      .addCase(createVoiceDiagnostic.fulfilled, (state, action) => {
        state.isVoiceDiagLoading = false;
        state.voiceDiagnostic = action.payload;
      })
      .addCase(createVoiceDiagnostic.rejected, (state, action) => {
        state.isVoiceDiagLoading = false;
        state.error = action.payload;
      })

      // ! Repair
      // Get repair info +
      .addCase(getRepair.pending, (state, action) => {
        state.isRepairLoading = true;
        state.error = null;
      })
      .addCase(getRepair.fulfilled, (state, action) => {
        state.isRepairLoading = false;
        state.repairDetails = action.payload;
      })
      .addCase(getRepair.rejected, (state, action) => {
        state.isRepairLoading = false;
        state.error = action.payload;
      })

      .addCase(updateRepair.pending, (state, action) => {
        state.isRepairLoading = true;
        state.error = null;
      })
      .addCase(updateRepair.fulfilled, (state, action) => {
        state.isRepairLoading = false;
      })
      .addCase(updateRepair.rejected, (state, action) => {
        state.isRepairLoading = false;
        state.error = action.payload;
      })
      .addCase(updateRepairPartStatus.pending, (state, action) => {
        state.isRepairLoading = true;
        state.error = null;
      })
      .addCase(updateRepairPartStatus.fulfilled, (state, action) => {
        state.isRepairLoading = false;
        state.repairDetails = {
          ...state.repairDetails, // зберігаємо всі інші поля (id, name тощо)
          parts: (state.repairDetails?.parts || []).map(part =>
            part.id === action.payload.id
              ? { ...part, work_status: action.payload.work_status }
              : part
          ),
        };
      })
      .addCase(updateRepairPartStatus.rejected, (state, action) => {
        state.isRepairLoading = false;
        state.error = action.payload;
      })
      .addCase(updateRepairServiceStatus.pending, (state, action) => {
        state.isRepairLoading = true;
        state.error = null;
      })
      .addCase(updateRepairServiceStatus.fulfilled, (state, action) => {
        state.isRepairLoading = false;
        state.repairDetails = {
          ...state.repairDetails,
          services: (state.repairDetails?.services || []).map(service =>
            service.id === action.payload.id
              ? { ...service, work_status: action.payload.work_status }
              : service
          ),
        };
      })
      .addCase(updateRepairServiceStatus.rejected, (state, action) => {
        state.isRepairLoading = false;
        state.error = action.payload;
      })

      // ! New car

      // create new car +
      .addCase(createNewCar.pending, (state, action) => {
        state.isSavingCarLoading = true;
        state.error = null;
      })
      .addCase(createNewCar.fulfilled, (state, action) => {
        state.isSavingCarLoading = false;
        state.newCar = action.payload.car_data;
        state.newCar.photo_url = action.payload.photo_url;
        state.newCar.car_id = action.payload.car_id;
      })
      .addCase(createNewCar.rejected, (state, action) => {
        state.isSavingCarLoading = false;
        state.error = action.payload;
      })

      // Upload car photos +
      .addCase(uploadCarPhotos.pending, (state, action) => {
        state.isSavingCarLoading = true;
        state.error = null;
      })
      .addCase(uploadCarPhotos.fulfilled, (state, action) => {
        state.isSavingCarLoading = false;
      })
      .addCase(uploadCarPhotos.rejected, (state, action) => {
        state.isSavingCarLoading = false;
        state.error = action.payload;
      })

      // Update existing car +
      .addCase(updateCar.pending, (state, action) => {
        state.isSavingCarLoading = true;
        state.error = null;
      })
      .addCase(updateCar.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cars = state.cars.map(car =>
          car.vehicle_id === action.payload.id
            ? {
              ...car,
             ...(action.payload.photo_url && { photo_url: action.payload.photo_url}),
                vehicle: { ...car.vehicle, ...action.payload.updated_fields },
              }
            : car
        );
      })
      .addCase(updateCar.rejected, (state, action) => {
        state.isSavingCarLoading = false;
        state.error = action.payload;
      })

      // get mileage or vin from photo +
      .addCase(getMileageOrVinFromPhoto.pending, state => {
        state.isMileageOrVinLoading = true;
        state.error = null;
      })
      .addCase(getMileageOrVinFromPhoto.fulfilled, (state, action) => {
        state.isMileageOrVinLoading = false;
        state.mileageOrVin = {
          ...(state.mileageOrVin || {}),
          ...action.payload,
        };
      })
      .addCase(getMileageOrVinFromPhoto.rejected, (state, action) => {
        state.isMileageOrVinLoading = false;
        state.error = action.payload;
      })

      // ! Upload media to convert
      .addCase(uploadMedia.pending, (state, action) => {
        state.isConvertingMedia = true;
        state.error = null;
      })
      .addCase(uploadMedia.fulfilled, (state, action) => {
        state.isConvertingMedia = false;
        state.convertedMedia = action.payload.urls;
      })
      .addCase(uploadMedia.rejected, (state, action) => {
        state.isConvertingMedia = false;
        state.error = action.payload;
      }),
});
export const {
  setChosenDate,
  clearChosenDate,
  deleteCarInfo,
  deleteMileageOrVin,
  clearDiag,
  clearRepair,
  deleteNewCar,
  clearVoiceDiag,
  clearCarData,
  clearMileageOrVin,
} = carsSlice.actions;

export default carsSlice.reducer;
